/**
 * Translated About page body.
 *
 * Mirrors the English CMS HTML for page_id "about": same wrapper, classes, images
 * and links, so existing styling applies unchanged. Proper nouns, award names,
 * institutions and published book/CD titles stay in English.
 */

const frenchAbout = (d) => `
<div class="legal-page container">
  <center>
    <h1>À propos de Cranford Publications</h1>
    <p class="intro-text">
      Fournisseur de beaux airs de violon depuis 1979. Plus de 40 ans consacrés à la préservation et
      au partage des traditions de la musique celtique pour violon du Cap-Breton, d'Irlande et
      d'Écosse.
    </p>
  </center>
  <hr class="divider">

  <section class="contact-section">
    <h2>🎻 L'histoire de Cranford Publications</h2>

    <div class="business-info-grid about-photo-grid">
      <div class="address-card">
        <img src="/api/assets/3333/thumbnail" alt="Paul Cranford jeune au phare avec son violon">
        <p style="font-size:0.85rem;color:var(--color-text-secondary);margin-top:0.5rem;text-align:center;font-style:italic;">Paul, jeune, au phare</p>
      </div>

      <div class="address-card">
        <img src="/api/assets/77/thumbnail" alt="Paul Cranford recevant le prix Katherine McLennan">
        <p style="font-size:0.85rem;color:var(--color-text-secondary);margin-top:0.5rem;text-align:center;font-style:italic;">Prix Katherine McLennan (2018)</p>
      </div>

      <div class="address-card">
        <img src="/api/assets/3385/thumbnail" alt="Paul Cranford en concert au festival Celtic Colours">
        <p style="font-size:0.85rem;color:var(--color-text-secondary);margin-top:0.5rem;text-align:center;font-style:italic;">Paul chez lui, octobre 2025</p>
      </div>
    </div>

    <div class="highlight-box">
      <p><strong>2024 a marqué la 45<sup>e</sup> année d'édition de Cranford.</strong></p>
      <p>
        En <strong>1979</strong>, le travail d'édition de Paul Cranford a commencé par la réédition de
        <em>The Skye Collection</em>, une compilation écossaise classique du 19<sup>e</sup> siècle.
        Ce fut le début de quatre décennies consacrées à la préservation et au partage des traditions
        musicales celtiques. Le prix Katherine McLennan souligne l'engagement de toute une vie de Paul
        à documenter et à préserver le riche patrimoine musical du Cap-Breton.
      </p>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>👤 Paul Stewart Cranford</h2>

    <div class="business-info-grid" style="margin-bottom:1.5rem;">
      <div class="info-item">
        <h4 style="margin-top:0;">🌊 Gardien de phare</h4>
        <p style="font-size:0.95rem;line-height:1.6;">34 ans (1975-2009) dans des stations maritimes isolées, mettant à profit la solitude pour la pratique, la recherche et la composition.</p>
      </div>
      <div class="info-item">
        <h4 style="margin-top:0;">🎻 Violoneux et compositeur</h4>
        <p style="font-size:0.95rem;line-height:1.6;">Des centaines de compositions originales inspirées du vent, des vagues et des traditions celtiques.</p>
      </div>
      <div class="info-item">
        <h4 style="margin-top:0;">📚 Éditeur et gardien du patrimoine</h4>
        <p style="font-size:0.95rem;line-height:1.6;">Plus de 12 livres documentant des milliers d'airs traditionnels et contemporains.</p>
      </div>
      <div class="info-item">
        <h4 style="margin-top:0;">🎓 Éducateur culturel</h4>
        <p style="font-size:0.95rem;line-height:1.6;">Reconnu à l'échelle internationale pour sa contribution à la préservation de la musique celtique.</p>
      </div>
    </div>

    <div class="info-box">
      <h3>Un parcours musical unique</h3>
      <p>
        Le propriétaire, Paul Stewart Cranford, est un violoneux, compositeur et gardien de phare
        retraité profondément enraciné dans les traditions de la musique qu'il vend, interprète et
        arrange. Sa connaissance intime des traditions celtiques vient de décennies de spectacles, de
        recherche et de travail de préservation.
      </p>
      <p style="margin-top:1rem;">
        La passion de Paul pour la musique de violon a commencé en 1975, à son arrivée en
        Nouvelle-Écosse depuis Toronto, sa ville natale. En une semaine, il a trouvé un emploi de
        gardien de phare et, peu après, il a adopté le violon comme instrument principal sans jamais
        revenir en arrière.
      </p>
      <p style="margin-top:1rem;">
        De 1976 à 1992, Paul a appris la musique irlandaise auprès du légendaire violoneux Johnny
        Wilmot, ajoutant une nouvelle dimension à son répertoire cap-bretonnais et écossais. Pendant
        ses 34 ans comme gardien de phare (1975-2009), l'horaire d'un mois sur deux lui a offert
        l'occasion idéale de parcourir le Cap-Breton et de rendre visite à des dizaines de musiciens
        plus âgés qui ont pris le temps de lui enseigner.
      </p>

      <div class="info-note" style="margin-top:1.5rem;">
        <strong>L'inspiration musicale de la mer :</strong> selon Paul, à l'époque du phare, beaucoup
        de ses airs lui sont venus alors qu'il marchait sur les rives et les plages en écoutant le
        vent et les vagues.
      </div>

      <h3>🌊 Les années au phare (1975-2009)</h3>
      <div class="info-box">
        <p>
          Paul a été gardien de phare pendant 34 ans, souvent affecté à des sites maritimes isolés.
          Ces affectations lui laissaient beaucoup de temps pour pratiquer le violon, composer et
          transcrire des airs à partir de vieux enregistrements. Sa dernière affectation était à
          <strong>Machias Seal Island</strong>, un sanctuaire d'oiseaux situé à la frontière
          canado-américaine, dans la baie de Fundy.
        </p>
        <p>
          📖 Pour en savoir plus sur la vie de Paul au phare, lisez
          <a href="https://lhdigest.com/Digest/StoryPage.cfm?StoryKey=1923" target="_blank" rel="noopener noreferrer">Lighthouse Digest Magazine (1996)</a>.
        </p>
      </div>

      <h3>🎼 Expertise musicale</h3>
      <p>
        En plus de rendre visite à des musiciens de la génération précédente comme Alex Francis
        MacKay, Hector MacKenzie, Robert Stubbert et d'autres spécialistes du style écossais du
        Cap-Breton, Paul a appris la musique irlandaise auprès du légendaire violoneux Johnny Wilmot
        de 1976 à 1992. Son travail ne se limite pas à la préservation : il comprend aussi
        l'interprétation et la composition, avec une compréhension profonde des contextes culturels
        qui ont façonné les diverses traditions musicales du Cap-Breton.
      </p>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>🏆 Reconnaissances et prix</h2>
    <h3>🎵 Jalons et distinctions de carrière</h3>
    <div class="business-info-grid" style="margin-bottom:1rem;">
      <div class="info-item">
        <div class="info-label">Celtic Colours</div>
        <div class="info-value">2009</div>
        <div class="info-subtext">Artiste en résidence</div>
      </div>
      <div class="info-item">
        <div class="info-label">CD More Tunes</div>
        <div class="info-value">2010</div>
        <div class="info-subtext">Plus de 100 compositions originales</div>
      </div>
      <div class="info-item">
        <div class="info-label">Journée mondiale du violon</div>
        <div class="info-value">2015</div>
        <div class="info-subtext">Nommé parrain</div>
      </div>
      <div class="info-item">
        <div class="info-label">Prix McLennan</div>
        <div class="info-value">2018</div>
        <div class="info-subtext">Préservation culturelle</div>
      </div>
    </div>

    <div class="info-box">
      <h4 style="color:var(--color-primary);margin-top:0;">Principales publications et réalisations</h4>
      <ul class="contact-list-compact">
        <li><strong>2009</strong> — Artiste en résidence au <a href="https://celtic-colours.com/artists/paul-cranford/" target="_blank" rel="noopener noreferrer">Celtic Colours International Festival</a></li>
        <li><strong>2010</strong> — Sortie du double CD <em>More Tunes from the Lighthouse</em>, comprenant plus de 100 compositions originales jamais enregistrées</li>
        <li><strong>2011</strong> — Réalisation de <em>The Celtic Colours Collection</em>, célébrant les 15 premières années du festival</li>
        <li><strong>2013</strong> — Publication de <em>The Cape Breton Scottish Collection</em></li>
        <li><strong>2015</strong> — Achèvement de <em>The Cape Breton Highland Collection</em>, des airs de pipes écossais arrangés pour violon. Nommé <a href="https://www.cbc.ca/islandechoes/blog/2015/05/19/paul-cranford---patron-of-world-fiddle-day/" target="_blank" rel="noopener noreferrer">parrain de la Journée mondiale du violon</a> (entrevue CBC)</li>
        <li><strong>2017</strong> — Honoré par The Gaelic College et Highland Village pour avoir documenté la tradition du violon au Cap-Breton</li>
        <li><strong>2018</strong> — Récipiendaire du prix Katherine McLennan, remis par The Fortress of Louisbourg Association, la Cape Breton Regional Library et le Beaton Institute de la CBU</li>
      </ul>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>🎯 Notre mission</h2>
    <div class="info-box">
      <p>
        Ce site fournit des ressources pour une tradition vivante en proposant des recueils de
        partitions et les enregistrements qui s'y rattachent. Nous croyons qu'il faut rendre la
        musique celtique traditionnelle accessible aux musiciens et aux passionnés du monde entier.
      </p>
      <p style="margin-top:1rem;">
        En explorant le site, vous trouverez des liens vers des milliers d'airs. Consultez n'importe
        quelle page présentant un livre ou un CD qui vous intéresse : la plupart contiennent des liens
        vers des airs. À mesure que les CD s'épuisent, nous continuons à publier du matériel
        d'accompagnement gratuit — partitions à l'écran et extraits sonores, ou liens vers des
        articles, des photos et des descriptions — tous liés aux livres et aux CD offerts dans notre
        ${d.store}.
      </p>
    </div>

    <div class="business-info-grid" style="margin-top:1.5rem;">
      <div class="info-item">
        <div class="info-label">Recueils de musique</div>
        <div class="info-value">12+</div>
        <div class="info-subtext">Titres publiés</div>
      </div>
      <div class="info-item">
        <div class="info-label">Collections d'airs</div>
        <div class="info-value">2500+</div>
        <div class="info-subtext">Airs traditionnels</div>
      </div>
      <div class="info-item">
        <div class="info-label">Expérience</div>
        <div class="info-value">40+</div>
        <div class="info-subtext">Années en activité</div>
      </div>
      <div class="info-item">
        <div class="info-label">En ligne depuis</div>
        <div class="info-value">1999</div>
        <div class="info-subtext">Plus de 25 ans en ligne</div>
      </div>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>🚀 Vers l'avenir</h2>
    <p>
      ${d.storeSecure} sert notre clientèle depuis 1999. Au fil du temps, nous continuons
      d'améliorer l'apparence, l'ergonomie et les fonctionnalités du site afin de le rendre plus
      accessible et mieux adapté aux appareils mobiles. Nous explorons également des façons d'offrir
      nos publications en format numérique.
    </p>

    <div class="highlight-box" style="margin-top:1.5rem;">
      <h3>Venez nous voir au Cap-Breton</h3>
      <p>
        Si vous visitez le Cap-Breton pendant les mois d'été, sachez que Paul habite en face de
        <strong>Wildfire Books and Pottery</strong>, sur la piste Cabot (44546 Cabot Trail, North
        Shore). La boutique tient un bon choix des livres de musique et des CD présentés sur ce site.
      </p>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>📞 Nous joindre</h2>
    <p class="contact-note-center">
      <strong>Des questions sur nos publications ou sur les traditions de la musique celtique ?</strong><br>
      Nous serions ravis de vous lire ! Visitez notre <a href="/page/contact">page Contact</a> pour
      nous joindre.
    </p>
    <p class="contact-note-center">
      Pour entendre Paul jouer au Cap-Breton, consultez son <a href="/page/paulsschedule">calendrier ici</a>.
    </p>
  </section>
</div>
`;

const germanAbout = `
<div class="legal-page container">
  <center>
    <h1>Über Cranford Publications</h1>
    <p class="intro-text">
      Lieferant feiner Fiddle-Melodien seit 1979. Über 40 Jahre Engagement für die Bewahrung und
      Verbreitung der keltischen Fiddle-Traditionen aus Cape Breton, Irland und Schottland.
    </p>
  </center>
  <hr class="divider">

  <section class="contact-section">
    <h2>🎻 Die Geschichte von Cranford Publications</h2>

    <div class="business-info-grid about-photo-grid">
      <div class="address-card">
        <img src="/api/assets/3333/thumbnail" alt="Der junge Paul Cranford mit seiner Fiddle am Leuchtturm">
        <p style="font-size:0.85rem;color:var(--color-text-secondary);margin-top:0.5rem;text-align:center;font-style:italic;">Der junge Paul am Leuchtturm</p>
      </div>

      <div class="address-card">
        <img src="/api/assets/77/thumbnail" alt="Paul Cranford bei der Verleihung des Katherine McLennan Award">
        <p style="font-size:0.85rem;color:var(--color-text-secondary);margin-top:0.5rem;text-align:center;font-style:italic;">Katherine McLennan Award (2018)</p>
      </div>

      <div class="address-card">
        <img src="/api/assets/3385/thumbnail" alt="Paul Cranford beim Auftritt bei Celtic Colours">
        <p style="font-size:0.85rem;color:var(--color-text-secondary);margin-top:0.5rem;text-align:center;font-style:italic;">Paul zu Hause, Oktober 2025</p>
      </div>
    </div>

    <div class="highlight-box">
      <p><strong>2024 war Cranfords 45. Jahr als Verlag.</strong></p>
      <p>
        <strong>1979</strong> begann Paul Cranfords Verlagsarbeit mit einer Neuauflage von
        <em>The Skye Collection</em>, einer klassischen schottischen Sammlung aus dem 19. Jahrhundert.
        Damit begannen vier Jahrzehnte, die der Bewahrung und Verbreitung keltischer Musiktraditionen
        gewidmet waren. Der Katherine McLennan Award ehrt Pauls lebenslangen Einsatz für die
        Dokumentation und Bewahrung des reichen musikalischen Erbes von Cape Breton.
      </p>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>👤 Paul Stewart Cranford</h2>

    <div class="business-info-grid" style="margin-bottom:1.5rem;">
      <div class="info-item">
        <h4 style="margin-top:0;">🌊 Leuchtturmwärter</h4>
        <p style="font-size:0.95rem;line-height:1.6;">34 Jahre (1975-2009) an abgelegenen Küstenstationen, wo er die Einsamkeit zum Üben, Forschen und Komponieren nutzte.</p>
      </div>
      <div class="info-item">
        <h4 style="margin-top:0;">🎻 Fiddler und Komponist</h4>
        <p style="font-size:0.95rem;line-height:1.6;">Hunderte eigene Kompositionen, inspiriert von Wind, Wellen und keltischen Traditionen.</p>
      </div>
      <div class="info-item">
        <h4 style="margin-top:0;">📚 Verleger und Bewahrer</h4>
        <p style="font-size:0.95rem;line-height:1.6;">Über 12 Bücher, die Tausende traditionelle und zeitgenössische Melodien dokumentieren.</p>
      </div>
      <div class="info-item">
        <h4 style="margin-top:0;">🎓 Kultureller Vermittler</h4>
        <p style="font-size:0.95rem;line-height:1.6;">International anerkannt für seine Beiträge zur Bewahrung keltischer Musik.</p>
      </div>
    </div>

    <div class="info-box">
      <h3>Ein einzigartiger musikalischer Weg</h3>
      <p>
        Inhaber Paul Stewart Cranford ist Fiddler, Komponist und Leuchtturmwärter im Ruhestand, der
        tief in den Musiktraditionen verwurzelt ist, die er verkauft, spielt und arrangiert. Seine
        genaue Kenntnis der keltischen Traditionen stammt aus Jahrzehnten des Auftretens, Forschens
        und Bewahrens.
      </p>
      <p style="margin-top:1rem;">
        Pauls Liebe zur Fiddle-Musik begann 1975 mit seiner Ankunft in Nova Scotia, das er aus seiner
        Heimatstadt Toronto erreichte. Innerhalb einer Woche fand er eine Stelle als
        Leuchtturmwärter, und schon kurz darauf wurde die Fiddle sein Hauptinstrument — für immer.
      </p>
      <p style="margin-top:1rem;">
        Von 1976 bis 1992 lernte Paul irische Musik beim legendären Fiddler Johnny Wilmot, was seinem
        Repertoire aus Cape Breton und Schottland eine weitere Dimension verlieh. Während seiner
        34 Jahre als Leuchtturmwärter (1975-2009) bot ihm der Dienstplan mit einem Monat Dienst und
        einem Monat frei die ideale Gelegenheit, Cape Breton weiträumig zu bereisen und Dutzende
        älterer Musiker zu besuchen, die sich Zeit nahmen, ihn zu unterrichten.
      </p>

      <div class="info-note" style="margin-top:1.5rem;">
        <strong>Musikalische Inspiration durch das Meer:</strong> Paul erzählt, dass ihm in seiner
        Zeit am Leuchtturm viele Melodien einfielen, während er an den Ufern und Stränden entlangging
        und Wind und Wellen lauschte.
      </div>

      <h3>🌊 Die Leuchtturmjahre (1975-2009)</h3>
      <div class="info-box">
        <p>
          Paul war 34 Jahre lang Leuchtturmwärter, oft an abgelegenen Küstenstandorten. Die
          einsamen Stationen gaben ihm viel Zeit, Fiddle zu üben, zu komponieren und Melodien von
          alten Aufnahmen zu transkribieren. Seine letzte Station war
          <strong>Machias Seal Island</strong>, ein Vogelschutzgebiet an der kanadisch-amerikanischen
          Grenze in der Bay of Fundy.
        </p>
        <p>
          📖 Mehr über Pauls Leben am Leuchtturm lesen Sie im
          <a href="https://lhdigest.com/Digest/StoryPage.cfm?StoryKey=1923" target="_blank" rel="noopener noreferrer">Lighthouse Digest Magazine (1996)</a>.
        </p>
      </div>

      <h3>🎼 Musikalische Expertise</h3>
      <p>
        Neben Besuchen bei Musikern der älteren Generation wie Alex Francis MacKay, Hector MacKenzie,
        Robert Stubbert und anderen, die auf den schottischen Stil von Cape Breton spezialisiert
        waren, lernte Paul von 1976 bis 1992 irische Musik beim legendären Fiddler Johnny Wilmot.
        Seine Arbeit umfasst nicht nur die Bewahrung, sondern auch das aktive Auftreten und
        Komponieren — mit einem tiefen Verständnis der kulturellen Zusammenhänge, die die
        verschiedenen Musiktraditionen von Cape Breton geprägt haben.
      </p>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>🏆 Auszeichnungen und Ehrungen</h2>
    <h3>🎵 Meilensteine und Anerkennungen</h3>
    <div class="business-info-grid" style="margin-bottom:1rem;">
      <div class="info-item">
        <div class="info-label">Celtic Colours</div>
        <div class="info-value">2009</div>
        <div class="info-subtext">Artist in Residence</div>
      </div>
      <div class="info-item">
        <div class="info-label">CD More Tunes</div>
        <div class="info-value">2010</div>
        <div class="info-subtext">Über 100 eigene Kompositionen</div>
      </div>
      <div class="info-item">
        <div class="info-label">World Fiddle Day</div>
        <div class="info-value">2015</div>
        <div class="info-subtext">Zum Schirmherrn berufen</div>
      </div>
      <div class="info-item">
        <div class="info-label">McLennan Award</div>
        <div class="info-value">2018</div>
        <div class="info-subtext">Kulturelle Bewahrung</div>
      </div>
    </div>

    <div class="info-box">
      <h4 style="color:var(--color-primary);margin-top:0;">Wichtige Publikationen und Projekte</h4>
      <ul class="contact-list-compact">
        <li><strong>2009</strong> — Artist in Residence beim <a href="https://celtic-colours.com/artists/paul-cranford/" target="_blank" rel="noopener noreferrer">Celtic Colours International Festival</a></li>
        <li><strong>2010</strong> — Veröffentlichung der Doppel-CD <em>More Tunes from the Lighthouse</em> mit über 100 bislang unveröffentlichten eigenen Kompositionen</li>
        <li><strong>2011</strong> — Produktion von <em>The Celtic Colours Collection</em> zum 15-jährigen Bestehen des Festivals</li>
        <li><strong>2013</strong> — Veröffentlichung von <em>The Cape Breton Scottish Collection</em></li>
        <li><strong>2015</strong> — Fertigstellung von <em>The Cape Breton Highland Collection</em> — schottische Dudelsackmelodien, für Fiddle arrangiert. Berufung zum <a href="https://www.cbc.ca/islandechoes/blog/2015/05/19/paul-cranford---patron-of-world-fiddle-day/" target="_blank" rel="noopener noreferrer">Schirmherrn des World Fiddle Day</a> (CBC-Interview)</li>
        <li><strong>2017</strong> — Ehrung durch The Gaelic College und Highland Village für die Dokumentation der Fiddle-Tradition von Cape Breton</li>
        <li><strong>2018</strong> — Verleihung des Katherine McLennan Award durch The Fortress of Louisbourg Association, die Cape Breton Regional Library und das Beaton Institute der CBU</li>
      </ul>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>🎯 Unsere Mission</h2>
    <div class="info-box">
      <p>
        Diese Website stellt Ressourcen für eine lebendige Tradition bereit: Notenbücher und die
        dazugehörigen Aufnahmen. Wir sind überzeugt, dass traditionelle keltische Musik für Musiker
        und Interessierte weltweit zugänglich sein sollte.
      </p>
      <p style="margin-top:1rem;">
        Beim Erkunden der Website finden Sie Links zu Tausenden Melodien. Sehen Sie sich jede Seite
        an, die ein Buch oder eine CD zeigt, die Sie interessiert — auf den meisten finden Sie Links
        zu Melodien. Wenn CDs nicht mehr erhältlich sind, stellen wir weiterhin kostenloses
        Begleitmaterial bereit: Notation und Hörbeispiele am Bildschirm oder Links zu Artikeln, Fotos
        und Beschreibungen, alle mit Bezug zu den Büchern und CDs in unserem Onlineshop.
      </p>
    </div>

    <div class="business-info-grid" style="margin-top:1.5rem;">
      <div class="info-item">
        <div class="info-label">Notenbücher</div>
        <div class="info-value">12+</div>
        <div class="info-subtext">Veröffentlichte Titel</div>
      </div>
      <div class="info-item">
        <div class="info-label">Melodiesammlungen</div>
        <div class="info-value">2500+</div>
        <div class="info-subtext">Traditionelle Melodien</div>
      </div>
      <div class="info-item">
        <div class="info-label">Erfahrung</div>
        <div class="info-value">40+</div>
        <div class="info-subtext">Jahre im Geschäft</div>
      </div>
      <div class="info-item">
        <div class="info-label">Online seit</div>
        <div class="info-value">1999</div>
        <div class="info-subtext">Über 25 Jahre online</div>
      </div>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>🚀 Ein Blick nach vorn</h2>
    <p>
      Der sichere Onlineshop bedient Kundinnen und Kunden seit 1999. Soweit es die Zeit erlaubt,
      verbessern wir weiterhin Aussehen, Bedienung und Funktionsumfang von Shop und Website, damit
      beide zugänglicher und mobilfreundlicher werden. Außerdem prüfen wir Wege, unsere Publikationen
      digital anzubieten.
    </p>

    <div class="highlight-box" style="margin-top:1.5rem;">
      <h3>Besuchen Sie uns in Cape Breton</h3>
      <p>
        Wenn Sie in den Sommermonaten durch Cape Breton reisen: Paul wohnt gegenüber von
        <strong>Wildfire Books and Pottery</strong> am Cabot Trail (44546 Cabot Trail, North Shore).
        Das Geschäft führt eine gute Auswahl der Notenbücher und CDs, die auf dieser Website
        vorgestellt werden.
      </p>
    </div>
  </section>

  <hr class="divider">

  <section class="contact-section">
    <h2>📞 Kontakt aufnehmen</h2>
    <p class="contact-note-center">
      <strong>Fragen zu unseren Publikationen oder zu keltischen Musiktraditionen?</strong><br>
      Wir hören gern von Ihnen! Besuchen Sie unsere <a href="/page/contact">Kontaktseite</a>.
    </p>
    <p class="contact-note-center">
      Wenn Sie Paul in Cape Breton spielen hören möchten, sehen Sie sich
      <a href="/page/paulsschedule">hier seinen Terminplan</a> an.
    </p>
  </section>
</div>
`;

export const ABOUT_PAGE_TRANSLATIONS = {
  'fr-CA': {
    title: 'À propos',
    description:
      'Cranford Publications — plus de 40 ans à préserver la musique celtique pour violon. Fondée en 1979 par Paul Stewart Cranford.',
    html: frenchAbout({
      store: 'magasin en ligne',
      storeSecure: 'Notre magasin en ligne sécurisé',
    }),
  },
  'fr-FR': {
    title: 'À propos',
    description:
      'Cranford Publications — plus de 40 ans à préserver la musique celtique pour violon. Fondée en 1979 par Paul Stewart Cranford.',
    html: frenchAbout({
      store: 'boutique en ligne',
      storeSecure: 'Notre boutique en ligne sécurisée',
    }),
  },
  de: {
    title: 'Über uns',
    description:
      'Cranford Publications — über 40 Jahre Bewahrung keltischer Fiddle-Musik. 1979 von Paul Stewart Cranford gegründet.',
    html: germanAbout,
  },
};
